import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useProgress } from '../contexts/ProgressContext';
import chaptersData from '../data/chapters.json';
import quizData from '../data/quizzes.json';
import UserMenu from '../components/UserMenu';

const QuizView = () => {
  const { day } = useParams();
  const navigate = useNavigate();
  const dayNumber = parseInt(day);
  
  const { 
    isChapterCompleted, 
    recordQuizScore,
    getQuizScore,
    isQuizAttempted 
  } = useProgress();

  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    // Check if chapter is completed
    if (!isChapterCompleted(dayNumber)) {
      navigate(`/chapter/${dayNumber}`);
      return;
    }

    // Load quiz questions from quizData
    const quizForDay = quizData.quizzes.find(q => q.day === dayNumber);
    if (quizForDay && quizForDay.questions) {
      setQuestions(quizForDay.questions);
    } else {
      // Fallback: create placeholder if quiz not yet created
      console.warn(`Quiz for day ${dayNumber} not found. Using placeholder.`);
      const placeholderQuestions = Array.from({ length: 10 }, (_, i) => ({
        id: i + 1,
        question: `Question ${i + 1} for Day ${dayNumber}: Quiz content is being prepared.`,
        options: [
          'This quiz is under development',
          'Please check back later',
          'Content will be available soon',
          'Thank you for your patience'
        ],
        correctAnswer: 0,
        explanation: 'This chapter\'s quiz questions are currently being developed based on the chapter content.'
      }));
      setQuestions(placeholderQuestions);
    }
  }, [dayNumber, isChapterCompleted, navigate]);

  const handleAnswerSelect = (optionIndex) => {
    if (!showExplanation) {
      setSelectedAnswers({
        ...selectedAnswers,
        [currentQuestion]: optionIndex
      });
      setShowExplanation(true);
    }
  };

  const handleNext = () => {
    setShowExplanation(false);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate final score
      let correctCount = 0;
      questions.forEach((q, index) => {
        if (selectedAnswers[index] === q.correctAnswer) {
          correctCount++;
        }
      });
      const finalScore = (correctCount / questions.length) * 100;
      setScore(finalScore);
      recordQuizScore(dayNumber, finalScore);
      setQuizComplete(true);
    }
  };

  const handleRetake = () => {
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowExplanation(false);
    setQuizComplete(false);
    setScore(0);
  };

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading quiz...</div>
      </div>
    );
  }

  if (quizComplete) {
    const passed = score >= 60;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-navy to-gray-900 flex items-center justify-center">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="bg-gray-800 rounded-xl p-8 shadow-2xl text-center">
            <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${
              passed ? 'bg-green-500' : 'bg-orange-500'
            }`}>
              {passed ? (
                <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <span className="text-4xl text-white">!</span>
              )}
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-4">
              Quiz Complete!
            </h2>
            
            <div className="mb-6">
              <div className="text-6xl font-bold mb-2" style={{ color: passed ? '#00a8a8' : '#f97316' }}>
                {score.toFixed(0)}%
              </div>
              <p className="text-gray-300 text-lg">
                You got {Math.round(score / 10)} out of {questions.length} questions correct
              </p>
            </div>

            {passed ? (
              <div className="bg-green-900/30 border border-green-500 rounded-lg p-4 mb-6">
                <p className="text-green-300">
                  Great job! You've passed the quiz. You can now proceed to the next chapter.
                </p>
              </div>
            ) : (
              <div className="bg-orange-900/30 border border-orange-500 rounded-lg p-4 mb-6">
                <p className="text-orange-300">
                  You scored below 60%. Consider reviewing the chapter and retaking the quiz.
                </p>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button
                onClick={handleRetake}
                className="w-full py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors"
              >
                ↻ Retake Quiz
              </button>
              <Link
                to={`/chapter/${dayNumber}`}
                className="w-full py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors text-center"
              >
                ← Back to Chapter
              </Link>
              {dayNumber < 30 && (
                <Link
                  to={`/chapter/${dayNumber + 1}`}
                  className="w-full py-3 bg-gradient-to-r from-teal to-accent text-white font-semibold rounded-lg hover:shadow-lg transition-all text-center"
                >
                  Next Chapter →
                </Link>
              )}
              <Link
                to="/"
                className="w-full py-3 bg-navy text-white font-semibold rounded-lg hover:bg-opacity-80 transition-colors text-center"
              >
                Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const selectedAnswer = selectedAnswers[currentQuestion];
  const isCorrect = selectedAnswer === question.correctAnswer;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-navy to-gray-900">
      {/* Progress Bar */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-semibold">Day {dayNumber} Quiz</span>
            <div className="flex items-center gap-3">
              <span className="text-gray-400">
                Question {currentQuestion + 1} of {questions.length}
              </span>
              <UserMenu />
            </div>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-teal h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Quiz Content */}
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="bg-gray-800 rounded-xl p-8 shadow-2xl">
          {/* Question */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-6">
              {question.question}
            </h2>

            {/* Options */}
            <div className="space-y-3">
              {question.options.map((option, index) => {
                const isSelected = selectedAnswer === index;
                const isCorrectAnswer = index === question.correctAnswer;
                
                let buttonClass = 'w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ';
                
                if (!showExplanation) {
                  buttonClass += isSelected 
                    ? 'border-teal bg-teal/20 text-white'
                    : 'border-gray-600 bg-gray-700 text-white hover:border-gray-500';
                } else {
                  if (isCorrectAnswer) {
                    buttonClass += 'border-green-500 bg-green-900/30 text-white';
                  } else if (isSelected && !isCorrect) {
                    buttonClass += 'border-red-500 bg-red-900/30 text-white';
                  } else {
                    buttonClass += 'border-gray-600 bg-gray-700 text-gray-400';
                  }
                }

                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    disabled={showExplanation}
                    className={buttonClass}
                  >
                    <div className="flex items-center">
                      <span className="font-semibold mr-3">
                        {String.fromCharCode(65 + index)}.
                      </span>
                      <span>{option}</span>
                      {showExplanation && isCorrectAnswer && (
                        <svg className="w-6 h-6 ml-auto text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {showExplanation && isSelected && !isCorrect && (
                        <svg className="w-6 h-6 ml-auto text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Explanation */}
          {showExplanation && (
            <div className={`mb-6 p-4 rounded-lg ${
              isCorrect ? 'bg-green-900/30 border border-green-500' : 'bg-orange-900/30 border border-orange-500'
            }`}>
              <h3 className={`font-semibold mb-2 ${isCorrect ? 'text-green-300' : 'text-orange-300'}`}>
                {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
              </h3>
              <p className="text-gray-300">{question.explanation}</p>
            </div>
          )}

          {/* Next Button */}
          {showExplanation && (
            <button
              onClick={handleNext}
              className="w-full py-3 bg-gradient-to-r from-teal to-accent text-white font-semibold rounded-lg hover:shadow-lg transition-all"
            >
              {currentQuestion < questions.length - 1 ? 'Next Question →' : 'Finish Quiz'}
            </button>
          )}

          {!showExplanation && selectedAnswer === undefined && (
            <p className="text-center text-gray-400">Select an answer to continue</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizView;
